import { useState, useEffect } from "react";
import { API_BASE_URL } from "../../../config";
import LoadingScreen from "../../common/LoadingScreen";
import { navigateToDashboard } from "../../../utils/navigationHelpers";
import { authFetch } from "../../../utils/auth";
import BackToDashboardButton from "../../common/BackToDashboardButton";
import "./../../../assets/css/dashboard.css";

// SVG Icons
const BookIcon = () => (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#e74c3c" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20" />
        <path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z" />
    </svg>
);

const StackIcon = () => (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#f39c12" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <polygon points="12 2 2 7 12 12 22 7 12 2" />
        <polyline points="2 17 12 22 22 17" />
        <polyline points="2 12 12 17 22 12" />
    </svg>
);

const ChartIcon = () => (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#2ecc71" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <line x1="18" y1="20" x2="18" y2="10" />
        <line x1="12" y1="20" x2="12" y2="4" />
        <line x1="6" y1="20" x2="6" y2="14" />
    </svg>
);

const SearchIcon = () => (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#95a5a6" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="11" cy="11" r="8" />
        <line x1="21" y1="21" x2="16.65" y2="16.65" />
    </svg>
);

const SortIcon = () => (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#95a5a6" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <line x1="4" y1="9" x2="14" y2="9" />
        <line x1="4" y1="5" x2="20" y2="5" />
        <line x1="4" y1="13" x2="10" y2="13" />
        <polyline points="18 10 22 14 18 18" />
        <line x1="22" y1="14" x2="14" y2="14" />
    </svg>
);

const ChevronIcon = ({ expanded }) => (
    <svg
        width="20"
        height="20"
        viewBox="0 0 24 24"
        fill="none"
        stroke="#7f8c8d"
        strokeWidth="2.5"
        strokeLinecap="round"
        strokeLinejoin="round"
        style={{
            transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)',
            transition: 'transform 0.25s ease-in-out'
        }}
    >
        <polyline points="6 9 12 15 18 9" />
    </svg>
);

function ReviewDash({ lang_id }) {
    const [weakWords, setWeakWords] = useState([]);
    const [cardCount, setCardCount] = useState(0);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(false);

    // UI controls
    const [searchQuery, setSearchQuery] = useState("");
    const [sortBy, setSortBy] = useState("errors_desc"); // "errors_desc", "fam_asc", "fam_desc", "alpha_asc"
    const [expandedWords, setExpandedWords] = useState({});
    const [currentPage, setCurrentPage] = useState(1);
    const itemsPerPage = 10;

    useEffect(() => {
        if (!lang_id) return;

        setLoading(true);
        // Request all weak words by using limit=all
        const fetchWeakWords = authFetch(`${API_BASE_URL}/api/weak-words?lang_id=${lang_id}&limit=all`)
            .then(res => res.json());

        const fetchReviewDeck = authFetch(`${API_BASE_URL}/api/review-deck?lang_id=${lang_id}&limit=30`)
            .then(res => res.json());

        Promise.all([fetchWeakWords, fetchReviewDeck])
            .then(([wordsData, deckData]) => {
                if (wordsData.error || deckData.error) {
                    setError(true);
                } else {
                    setWeakWords(wordsData.weak_words || []);
                    setCardCount(deckData.cards ? deckData.cards.length : 0);
                }
            })
            .catch(err => {
                console.error("Error loading review info:", err);
                setError(true);
            })
            .finally(() => {
                setLoading(false);
            });
    }, [lang_id]);

    const handleStartReview = () => {
        window.location.href = `/review`;
    };

    const toggleExpand = (word) => {
        setExpandedWords(prev => ({
            ...prev,
            [word]: !prev[word]
        }));
    };

    if (loading) {
        return <LoadingScreen inline title="Loading review summary..." size={50} />;
    }

    if (error) {
        return (
            <div className="review-error">
                <h3>Something went wrong</h3>
                <p>There was an error loading your review data. Please try again later.</p>
            </div>
        );
    }

    if (cardCount === 0) {
        return (
            <div className="review-dash-root">
                <div className="review-empty-caughtup">
                    <div className="review-empty-caughtup__icon">
                        <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="#2ecc71" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
                            <polyline points="22 4 12 14.01 9 11.01" />
                        </svg>
                    </div>
                    <h2>All Caught Up!</h2>
                    <p>No weak cards to review right now.</p>
                    <BackToDashboardButton
                        centered
                        onClick={() => navigateToDashboard("vocabulary", { langId: lang_id })}
                    />
                </div>
            </div>
        );
    }

    // Calculations
    const avgFamTotal = weakWords.length > 0 ? (weakWords.reduce((sum, item) => sum + item.avg_familiarity, 0) / weakWords.length) * 100 : 0;

    // Filter and Sort
    const processedWords = weakWords
        .filter(item => item.word.toLowerCase().includes(searchQuery.toLowerCase()))
        .sort((a, b) => {
            if (sortBy === "errors_desc") {
                return b.count - a.count;
            } else if (sortBy === "fam_asc") {
                return a.avg_familiarity - b.avg_familiarity;
            } else if (sortBy === "fam_desc") {
                return b.avg_familiarity - a.avg_familiarity;
            } else if (sortBy === "alpha_asc") {
                return a.word.localeCompare(b.word);
            }
            return 0;
        });

    const totalPages = Math.ceil(processedWords.length / itemsPerPage);
    const startIndex = (currentPage - 1) * itemsPerPage;
    const paginatedWords = processedWords.slice(startIndex, startIndex + itemsPerPage);

    return (
        <div className="review-dash-root">
            <div className="review-action-bar">
                <button className="review-start-btn" onClick={handleStartReview}>
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                        <polygon points="5 3 19 12 5 21 5 3" />
                    </svg>
                    Start Review Session
                </button>
            </div>

            {/* High-Level Stats Panel */}
            <div className="review-stats-grid">
                <div className="review-stat-card">
                    <div className="review-stat-icon" style={{ background: 'rgba(231, 76, 60, 0.1)' }}>
                        <BookIcon />
                    </div>
                    <div>
                        <div className="review-stat-label">Troubled Vocabulary</div>
                        <div className="review-stat-value">{weakWords.length} words</div>
                    </div>
                </div>

                <div className="review-stat-card">
                    <div className="review-stat-icon" style={{ background: 'rgba(243, 156, 18, 0.1)' }}>
                        <StackIcon />
                    </div>
                    <div>
                        <div className="review-stat-label">Review Deck Size</div>
                        <div className="review-stat-value">{cardCount} cards</div>
                    </div>
                </div>

                <div className="review-stat-card">
                    <div className="review-stat-icon" style={{ background: 'rgba(46, 204, 113, 0.1)' }}>
                        <ChartIcon />
                    </div>
                    <div>
                        <div className="review-stat-label">Average Familiarity</div>
                        <div className="review-stat-value">{Math.round(avgFamTotal)}%</div>
                    </div>
                </div>
            </div>

            {/* Filtering and Sorting Controls */}
            <div className="review-controls-row">
                <div className="review-search-wrap">
                    <div className="review-search-icon">
                        <SearchIcon />
                    </div>
                    <input
                        type="text"
                        className="review-search-input"
                        placeholder="Search weak words..."
                        value={searchQuery}
                        onChange={(e) => {
                            setSearchQuery(e.target.value);
                            setCurrentPage(1);
                        }}
                    />
                </div>

                <div className="review-sort-wrap">
                    <SortIcon />
                    <select
                        className="review-sort-select"
                        value={sortBy}
                        onChange={(e) => {
                            setSortBy(e.target.value);
                            setCurrentPage(1);
                        }}
                    >
                        <option value="errors_desc">Sort by Error Count (High to Low)</option>
                        <option value="fam_asc">Sort by Familiarity (Low to High)</option>
                        <option value="fam_desc">Sort by Familiarity (High to Low)</option>
                        <option value="alpha_asc">Sort Alphabetically (A-Z)</option>
                    </select>
                </div>
            </div>

            {/* List of Weak Words */}
            <div className="review-word-list">
                {paginatedWords.map((item, index) => {
                    const isExpanded = !!expandedWords[item.word];

                    // Style attributes based on familiarity score
                    let statusText = "Needs Work";
                    let statusColor = "#f39c12";
                    let statusBg = "rgba(243, 156, 18, 0.08)";

                    if (item.avg_familiarity < 0.2) {
                        statusText = "Critical";
                        statusColor = "#e74c3c";
                        statusBg = "rgba(231, 76, 60, 0.08)";
                    } else if (item.avg_familiarity > 0.5) {
                        statusText = "Reviewing";
                        statusColor = "#3498db";
                        statusBg = "rgba(52, 152, 219, 0.08)";
                    }

                    return (
                        <div key={index} className="word-card-item">
                            <div className="word-header-click" onClick={() => toggleExpand(item.word)}>
                                <div className="word-header-left">
                                    <span className="word-header-title">{item.word}</span>
                                    <span className="word-status-badge" style={{ background: statusBg, color: statusColor }}>
                                        {statusText}
                                    </span>
                                    {item.is_song && (
                                        <span style={{
                                            fontSize: '0.78rem',
                                            fontWeight: '600',
                                            background: '#eff6ff',
                                            color: '#2563eb',
                                            padding: '2px 8px',
                                            borderRadius: '6px'
                                        }}>
                                            Song: {item.song_title || 'From Song'}
                                        </span>
                                    )}
                                </div>

                                <div className="word-header-right">
                                    {/* Familiarity Bar */}
                                    <div className="familiarity-bar-wrap">
                                        <div className="familiarity-bar-labels">
                                            <span>Familiarity</span>
                                            <span style={{ color: statusColor, fontWeight: 'bold' }}>{Math.round(item.avg_familiarity * 100)}%</span>
                                        </div>
                                        <div className="familiarity-bar-track">
                                            <div className="familiarity-bar-fill" style={{ width: `${item.avg_familiarity * 100}%`, background: statusColor }} />
                                        </div>
                                    </div>

                                    {/* Error contexts count badge */}
                                    <div className="error-count-badge">
                                        {item.count} {item.count === 1 ? 'error context' : 'error contexts'}
                                    </div>

                                    <ChevronIcon expanded={isExpanded} />
                                </div>
                            </div>

                            {/* Expanded contextual details */}
                            {isExpanded && (
                                <div className="word-expanded-panel">
                                    <div className="word-expanded-inner">
                                        <div className="word-expanded-heading">
                                            Troubled Sentence Contexts
                                        </div>
                                        <div className="prompt-list">
                                            {item.prompts.map((p, pIndex) => (
                                                <div key={pIndex} className="prompt-row">
                                                    <div className="prompt-info">
                                                        <div className="prompt-info__text">{p.prompt}</div>
                                                        <div className="prompt-info__translation">{p.translation}</div>
                                                    </div>
                                                    <div className="prompt-meta">
                                                        {p.is_song && (
                                                            <span style={{ fontSize: '0.76rem', color: '#2563eb', fontWeight: '600', marginRight: '6px' }}>
                                                                Song: {p.song_title || 'Song'}
                                                            </span>
                                                        )}
                                                        <div className="prompt-meta__date">
                                                            {p.last_practiced ? `Last: ${new Date(p.last_practiced).toLocaleDateString()}` : 'Not practiced'}
                                                        </div>
                                                        <div
                                                            className="familiar-badge"
                                                            style={{
                                                                background: p.familiarity_score > 0.5 ? 'rgba(46, 204, 113, 0.1)' : 'rgba(231, 76, 60, 0.1)',
                                                                color: p.familiarity_score > 0.5 ? '#27ae60' : '#c0392b'
                                                            }}
                                                        >
                                                            {Math.round(p.familiarity_score * 100)}% Familiar
                                                        </div>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                    );
                })}

                {/* Empty State */}
                {processedWords.length === 0 && (
                    <div className="review-empty-search">
                        <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#bdc3c7" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                            <circle cx="11" cy="11" r="8" />
                            <line x1="21" y1="21" x2="16.65" y2="16.65" />
                            <line x1="8" y1="11" x2="14" y2="11" />
                        </svg>
                        <h3>No matching words found</h3>
                        <p>Try adjusting your search term to find troubled vocabulary.</p>
                    </div>
                )}

                {/* Pagination Controls */}
                {totalPages > 1 && (
                    <div className="pagination-row">
                        <button
                            className="pagination-btn"
                            onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                            disabled={currentPage === 1}
                        >
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                <polyline points="15 18 9 12 15 6" />
                            </svg>
                            Previous
                        </button>
                        <span className="pagination-info">
                            Page {currentPage} of {totalPages} ({processedWords.length} words found)
                        </span>
                        <button
                            className="pagination-btn"
                            onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                            disabled={currentPage === totalPages}
                        >
                            Next
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                <polyline points="9 18 15 12 9 6" />
                            </svg>
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
}

export default ReviewDash;
